/**
 * useChangelog / useChangelogEntry — read layer for the What's New log
 * (RW-1642). Reads through changelogService (the changelogApi callable; no
 * client DB path — RW-823) and stays live via the signals/{tenant}__changelog
 * doc: refetch whenever `rev` moves (deploy published, flag flipped,
 * hide/reword). Lifted from useContacts.js.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribe } from '../services/data';
import { useTenant } from '../contexts/TenantContext';
import { changelogService } from '../services/changelog';
import { signalError } from './signalListenerError';

/** Track the changelog signal revision for the current tenant (0 until first snapshot). */
export function useChangelogSignalRev() {
  const { tenantId } = useTenant();
  const [rev, setRev] = useState(0);
  useEffect(() => {
    if (!tenantId) return undefined;
    const unsub = subscribe(
      `signals/${tenantId}__changelog`,
      (snap) => { if (snap && typeof snap.rev === 'number') setRev(snap.rev); },
      signalError('changelog'),
    );
    return unsub;
  }, [tenantId]);
  return rev;
}

/** Visible entries + roadmap, kept live on the signal doc. */
export function useChangelog({ enabled = true, onError } = {}) {
  const [entries, setEntries] = useState([]);
  const [roadmap, setRoadmap] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const rev = useChangelogSignalRev();
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const refetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const data = await changelogService.list();
      setEntries(data?.entries || []);
      setRoadmap(data?.roadmap || []);
      setError(null);
    } catch (err) {
      setError(err);
      if (onErrorRef.current) onErrorRef.current(err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { refetch(); }, [refetch, rev]);

  return { entries, roadmap, loading, error, refetch };
}

/** One entry + its same-release siblings, live on the signal doc. */
export function useChangelogEntry(id, { onError } = {}) {
  const [entry, setEntry] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rev = useChangelogSignalRev();
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const refetch = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await changelogService.get(id);
      setEntry(data?.entry || null);
      setSiblings(data?.siblings || []);
      setError(null);
    } catch (err) {
      setError(err);
      setEntry(null);
      if (onErrorRef.current) onErrorRef.current(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refetch(); }, [refetch, rev]);

  return { entry, siblings, loading, error, refetch };
}
